import { Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import { Home, Key } from '@portals/icons';
import type { MarketingType, ObjectType } from '@portals/sip-client-data/src/general/ApiClientTypes';
import { useFormikContext } from 'formik';
import { getFixedT } from 'i18next';
import React, { useCallback } from 'react';

import { ControlledSelectField, type ControlledSelectFieldOptionsProps } from '../../../../components';
import { getAllMarketingTypes } from '../../../../config';
import { getObjectTypeOptions } from '../../../../utils';

const t = getFixedT(null, 'core-immobilien');

interface Props {
  marketingTypeFieldName: string;
  objectTypeFieldName: string;
}

export const MarketingTypeObjectTypeFilter: React.FC<Props> = ({ marketingTypeFieldName, objectTypeFieldName }) => {
  const { values, setFieldTouched, setFieldValue } = useFormikContext<any>();
  const marketingValue = values[marketingTypeFieldName] as MarketingType;
  const objectTypeValue = values[objectTypeFieldName] as ObjectType;

  const onMarketingTypeChange = useCallback(
    (newMarketing: MarketingType) => {
      setFieldTouched(marketingTypeFieldName, true);
      setFieldValue(marketingTypeFieldName, newMarketing, true);

      const objectTypeOptions = getObjectTypeOptions(t, newMarketing);
      const stillValid = objectTypeOptions.some((o) => o.value === objectTypeValue);
      const newObjectType = stillValid
        ? objectTypeValue
        : objectTypeOptions.length > 0
        ? objectTypeOptions[0].value
        : '';

      setFieldTouched(objectTypeFieldName, true);
      setFieldValue(objectTypeFieldName, newObjectType, true);
    },
    [marketingTypeFieldName, objectTypeFieldName, objectTypeValue, setFieldTouched, setFieldValue]
  );

  const onObjectTypeChange = useCallback(
    (newObjectType: ObjectType) => {
      setFieldTouched(objectTypeFieldName, true);
      setFieldValue(objectTypeFieldName, newObjectType, true);
    },
    [objectTypeFieldName, setFieldTouched, setFieldValue]
  );

  return (
    <Box sx={{ px: 6 }}>
      <Typography variant="h3">{t('objectType')}</Typography>
      <Grid container rowSpacing={5}>
        <Grid size={12}>
          <ControlledSelectField
            testId={marketingTypeFieldName}
            label={t('estateSearch.usageType')}
            icon={<Key />}
            items={getAllMarketingTypes(t) as ControlledSelectFieldOptionsProps[]}
            selectedValue={marketingValue}
            onChange={onMarketingTypeChange}
          />
        </Grid>
        <Grid size={12}>
          <ControlledSelectField
            testId={objectTypeFieldName}
            label={t('what')}
            icon={<Home />}
            items={getObjectTypeOptions(t, marketingValue) as ControlledSelectFieldOptionsProps[]}
            selectedValue={objectTypeValue}
            onChange={onObjectTypeChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

MarketingTypeObjectTypeFilter.displayName = 'MarketingTypeObjectTypeFilter';
